import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { CustomFormField } from "components/custom-form/CustomForm";
import card_services from "services/card_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";

export interface UseReplaceCardReturn {
  fields: CustomFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  showSuccess: boolean;
  successMsg: string;
  /** A non-fatal caveat riding along with the success (e.g. the cardholder's account record
   *  couldn't be synced to the new card number) — set only when the response carries one. */
  successWarning: string;
  handleSubmit: () => Promise<void>;
  handleRetry: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
}

/**
 * ReplaceCardForm is mounted only while its modal is open (see CardRow), so this hook's state
 * resets for free on every open — no explicit reset() needed here, unlike useIssueCard.
 */
export function useReplaceCard(
  initialOldCardNumber: string,
): UseReplaceCardReturn {
  const networkType = useSelector(networkTypeSelector);
  const queryClient = useQueryClient();

  const [oldCardNumber, setOldCardNumber] = useState(initialOldCardNumber);
  const [newCardNumber, setNewCardNumber] = useState("");

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [successWarning, setSuccessWarning] = useState("");

  const newCardNumberRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
    setSuccessWarning("");
  };

  const focusNext =
    (ref: React.RefObject<HTMLInputElement>) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter") ref.current?.focus();
    };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const handleSubmit = async () => {
    handleCloseError();

    const oldTrimmed = oldCardNumber.trim();
    const newTrimmed = newCardNumber.trim();

    if (!oldTrimmed || !newTrimmed) {
      return openError("Enter both the old and the new card number.");
    }
    if (oldTrimmed === newTrimmed) {
      return openError(
        "The new card number must be different from the old one.",
      );
    }

    try {
      setLoadingMsg("Replacing card…");
      setShowLoading(true);

      const result = await card_services.replaceCard(
        oldTrimmed,
        newTrimmed,
        networkType,
      );

      // The list's balances/status for both the retired and the new card are now stale.
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      // Marked stale but NOT refetched: useCardLookup reports isFetching as "loading", which
      // swaps the searched row for a skeleton and unmounts the success modal below (see
      // useCardStatusToggle, which hit the same bug first).
      queryClient.invalidateQueries({
        queryKey: ["card-lookup"],
        refetchType: "none",
      });
      queryClient.invalidateQueries({ queryKey: ["balances", "cards"] });

      setShowLoading(false);
      setSuccessMsg(`Card ${oldTrimmed} has been replaced by card ${newTrimmed}.`);
      setSuccessWarning(result?.warning ?? "");
      setShowSuccess(true);
    } catch (error: any) {
      openError(
        apiErrorMessage(error, "Failed to replace card. Please try again."),
      );
    }
  };

  const handleRetry = handleSubmit;

  const fields: CustomFormField[] = [
    {
      kind: "text",
      name: "oldCardNumber",
      label: "Old Card Number",
      value: oldCardNumber,
      onChange: setOldCardNumber,
      onKeyDown: focusNext(newCardNumberRef),
      placeholder: "XXXX XXXX XXXX XXXX",
      autoComplete: "off",
    },
    {
      kind: "text",
      name: "newCardNumber",
      label: "New Card Number",
      value: newCardNumber,
      onChange: setNewCardNumber,
      onKeyDown: submitOnEnter,
      placeholder: "XXXX XXXX XXXX XXXX",
      autoComplete: "off",
      inputRef: newCardNumberRef,
    },
  ];

  const isSubmitDisabled =
    !oldCardNumber.trim() ||
    !newCardNumber.trim() ||
    oldCardNumber.trim() === newCardNumber.trim();

  return {
    fields,
    submitRef,
    isSubmitDisabled,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    showSuccess,
    successMsg,
    successWarning,
    handleSubmit,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  };
}
